/**
 * Helpers for the temporary `commentContent measured` analytics event, which records how tall
 * comments render so the "Show more" collapse cutoff can be chosen from real data.
 *
 * Everything here returns numbers, booleans, or fixed enum strings. Never add comment text or other
 * user-generated content to these results.
 */

export type CommentRenderLocation = 'pageFooter' | 'standalone' | 'commentsPanel' | 'other';

export const getCommentRenderLocation = ({
	isFooterComment,
	isStandaloneComment,
	isCommentsPanel,
}: {
	isFooterComment: boolean;
	isStandaloneComment: boolean;
	isCommentsPanel: boolean;
}): CommentRenderLocation => {
	// Footer comments also pass `isCommentsPanel`, so check the footer flag first.
	if (isFooterComment) return 'pageFooter';
	if (isStandaloneComment) return 'standalone';
	if (isCommentsPanel) return 'commentsPanel';
	return 'other';
};

export type CommentContentStats = {
	textLength: number;
	topLevelNodeCount: number;
	hasMedia: boolean;
	hasTable: boolean;
	hasCodeBlock: boolean;
	hasExtension: boolean;
	hasList: boolean;
	hasSmartCard: boolean;
};

export type AdfNode = {
	type?: string;
	text?: string;
	content?: AdfNode[];
};

const MEDIA_NODE_TYPES = new Set(['media', 'mediaSingle', 'mediaGroup', 'mediaInline']);
const EXTENSION_NODE_TYPES = new Set([
	'extension',
	'bodiedExtension',
	'inlineExtension',
	'multiBodiedExtension',
]);
const LIST_NODE_TYPES = new Set(['bulletList', 'orderedList', 'taskList', 'decisionList']);
const SMART_CARD_NODE_TYPES = new Set(['inlineCard', 'blockCard', 'embedCard']);

/**
 * Summarises an ADF document without exposing its content. Uses an explicit stack rather than
 * recursion so deeply nested documents cannot overflow the call stack.
 */
export const getCommentContentStats = (adf: AdfNode | null | undefined): CommentContentStats => {
	const stats: CommentContentStats = {
		textLength: 0,
		topLevelNodeCount: adf?.content?.length ?? 0,
		hasMedia: false,
		hasTable: false,
		hasCodeBlock: false,
		hasExtension: false,
		hasList: false,
		hasSmartCard: false,
	};

	const stack: AdfNode[] = adf?.content ? [...adf.content] : [];
	while (stack.length > 0) {
		const node = stack.pop();
		if (!node || typeof node !== 'object') continue;

		const type = node.type ?? '';
		if (type === 'text' && typeof node.text === 'string') {
			stats.textLength += node.text.length;
		}
		if (MEDIA_NODE_TYPES.has(type)) stats.hasMedia = true;
		if (type === 'table') stats.hasTable = true;
		if (type === 'codeBlock') stats.hasCodeBlock = true;
		if (EXTENSION_NODE_TYPES.has(type)) stats.hasExtension = true;
		if (LIST_NODE_TYPES.has(type)) stats.hasList = true;
		if (SMART_CARD_NODE_TYPES.has(type)) stats.hasSmartCard = true;

		if (Array.isArray(node.content)) {
			stack.push(...node.content);
		}
	}

	return stats;
};

export type CommentContentMeasurement = {
	/** Full height of the rendered comment content, including any part hidden by truncation. */
	renderedHeight: number;
	/** Height of the comment body as the user sees it, after any truncation or collapse. */
	visibleHeight: number;
	renderedWidth: number;
	isTruncated: boolean;
};

// Class the ADF renderer puts on its document root. It sits inside the truncation and "Show more"
// wrappers, and an element's own box is not shrunk by ancestors that clip overflow, so its height is
// the full content height even while the comment is collapsed.
const RENDERER_DOCUMENT_SELECTOR = '.ak-renderer-document';

// Allow for sub-pixel rounding before calling a comment truncated.
const TRUNCATION_TOLERANCE_PX = 1;

/**
 * Measures a rendered comment. Returns null when there is nothing meaningful to record yet, for
 * example when the renderer has not mounted or the comment is hidden with zero size.
 */
export const measureCommentContent = (container: HTMLElement): CommentContentMeasurement | null => {
	const documentElement = container.querySelector<HTMLElement>(RENDERER_DOCUMENT_SELECTOR);
	if (!documentElement) return null;

	const contentRect = documentElement.getBoundingClientRect();
	if (contentRect.height <= 0 || contentRect.width <= 0) return null;

	const renderedHeight = Math.round(contentRect.height);
	const visibleHeight = Math.round(container.getBoundingClientRect().height);

	return {
		renderedHeight,
		visibleHeight,
		renderedWidth: Math.round(contentRect.width),
		isTruncated: renderedHeight - visibleHeight > TRUNCATION_TOLERANCE_PX,
	};
};
